import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  EMPTY,
  finalize,
  Observable,
  shareReplay,
  take,
  tap,
} from 'rxjs';
import {
  ApiService,
  GenericResponseCode,
  GenericResponseResult,
} from '@brighthub/api';

import { normalizeClientGuid } from './client-guid.util';
import {
  ClientSubjectData,
  UiSession,
  UiSessionResponse,
  UiSessionsResponse,
} from './ui-session.model';

import { CopilotFacadeService } from '@brighthub/copilot';
import { MessageService } from 'primeng/api';
import { AmplitudeService, SentryService } from '@brighthub/logging';
import { AuthService, OktaUserCacheService } from '@brightside/core/auth';

@Injectable({
  providedIn: 'root',
})
export class ClientSessionService {
  private _sessions = signal<UiSession[]>([]);
  readonly sessions = computed(() =>
    [...this._sessions()].sort(
      (a, b) =>
        new Date(b[this._sortBy()]).getTime() -
        new Date(a[this._sortBy()]).getTime(),
    ),
  );
  private _archivedSessions = signal<UiSession[]>([]);
  readonly archivedSessions = computed(() =>
    [...this._archivedSessions()].sort(
      (a, b) =>
        new Date(b[this._sortBy()]).getTime() -
        new Date(a[this._sortBy()]).getTime(),
    ),
  );
  private _loading = signal(true);
  private _sortBy = signal<'createdDate'>('createdDate');
  private _api = inject(ApiService);
  private _facade = inject(CopilotFacadeService);
  private readonly _messageService = inject(MessageService);
  private _amplitude = inject(AmplitudeService);
  private _sentry = inject(SentryService);
  private _selectedSubjectData = signal<ClientSubjectData | null>(null);
  readonly selectedSubjectData = this._selectedSubjectData.asReadonly();
  private _selectedSession = signal<UiSession | null>(null);
  readonly selectedSession = this._selectedSession.asReadonly();

  private _showArchived = signal(false);
  readonly showArchived = this._showArchived.asReadonly();
  private _selectedConversationId = signal<string | null>(null);
  readonly selectedConversationId = this._selectedConversationId.asReadonly();

  private _lastActiveSession = signal<UiSession | null>(null);
  private _lastArchivedSession = signal<UiSession | null>(null);
  private _initialized = signal(false);
  readonly initialized = this._initialized.asReadonly();

  /** Normalized GUID from an inbound Salesforce (or similar) deep link; blocks default auto-select until cleared. */
  private _pendingDeepLinkGuid = signal<string | null>(null);

  readonly auth = inject(AuthService);
  readonly loggedInUserId = signal('');

  private _oktaUserCache = inject(OktaUserCacheService);
  private _channel = new BroadcastChannel('brightnav-sessions');
  /** Retryable BTM idempotency keys for first-time opens (no uiSessionId yet). */
  private _openAttemptIds = new Map<string, string>();
  /** Shared create requests so overlapping opens cannot mint duplicate rows. */
  private _openAttempts = new Map<string, Observable<UiSessionResponse>>();

  get isReady() {
    return this.initialized;
  }

  toggleArchivedView() {
    this._showArchived.set(!this._showArchived());
  }

  setArchivedView(value: boolean) {
    this._showArchived.set(value);
  }

  constructor() {
    effect(() => {
      const _ = this._facade.clearContextSignal();
      // this._selectedSession.set(null);
      this.setSelectedSubject(null);
      // this._selectedConversationId.set(null);
    });

    this._channel.onmessage = (event) => {
      const { type, payload } = event.data ?? {};
      if (!type || !payload) return;

      switch (type) {
        case 'setSessions':
          this._sessions.set(payload);

          break;
        case 'setArchivedSessions':
          this._archivedSessions.set(payload);
          break;
      }
    };

    effect(() => {
      const sessions = this._sessions();
      const archived = this._archivedSessions();
      const selected = this._selectedSession();

      if (
        selected &&
        !sessions.some((s) => s.uiSessionId === selected.uiSessionId) &&
        !archived.some((s) => s.uiSessionId === selected.uiSessionId)
      ) {
        this._facade.clearContext();
      }
    });

    this.auth.getUser().then((user) => {
      if (user) {
        this.loggedInUserId.set(user.sub);
      }
    });
  }

  setSelectedConversation(id: string | null) {
    this._selectedConversationId.set(id);
  }

  rememberLastSession(session: UiSession) {
    if (session.state === 'archived') {
      this._lastArchivedSession.set(session);
    } else {
      this._lastActiveSession.set(session);
    }
  }

  restorePreviousSessionOrClear(view: 'active' | 'archived') {
    const session =
      view === 'archived'
        ? this._lastArchivedSession()
        : this._lastActiveSession();

    if (session) {
      this.createOrSelectClientConversation(session);
    } else {
      this._facade.clearContext();
    }
  }

  findActiveSessionForSubject(subjectId: string): UiSession | null {
    return (
      this._sessions().find(
        (s) => s.subjectId === subjectId && s.state === 'active',
      ) ?? null
    );
  }

  /**
   * Returns the active UI session for a client account GUID, if one exists.
   * Sessions are sorted by {@link sessions} (newest first); at most one active session per GUID is expected.
   *
   * @param guid Raw or normalized client account GUID.
   */
  findActiveSessionByGuid(guid: string): UiSession | null {
    const target = normalizeClientGuid(guid);
    if (!target) {
      return null;
    }
    return (
      this.sessions().find(
        (s) =>
          s.state === 'active' &&
          normalizeClientGuid(s.subjectData.guid) === target &&
          !!s.conversationId,
      ) ??
      this.sessions().find(
        (s) =>
          s.state === 'active' &&
          normalizeClientGuid(s.subjectData.guid) === target,
      ) ??
      null
    );
  }

  /**
   * Registers a pending deep-link GUID so {@link loadClientSessions} does not auto-select the most recent thread.
   *
   * @remarks Used when `?guid=` is present on the BrightNav route (e.g. Salesforce links).
   * @param guid Raw or normalized client GUID, or null to clear; stored in normalized form.
   */
  setPendingDeepLinkGuid(guid: string | null): void {
    this._pendingDeepLinkGuid.set(normalizeClientGuid(guid));
  }

  setSelectedSubject(subjectData: ClientSubjectData | null) {
    this._selectedSubjectData.set(subjectData);
  }

  get loading() {
    return this._loading.asReadonly();
  }

  setSortBy(field: 'createdDate') {
    this._sortBy.set(field);
  }

  loadClientSessions(): void {
    this._loading.set(true);

    this.getUiSession('?subjectType=client').subscribe({
      next: (res) => {
        const activeSessions = (res.payload.uiSessions ?? []).filter(
          (s: UiSession) => {
            return s.state === 'active' && this.hasSubjectData(s.subjectData);
          },
        );
        const archivedSessions = (res.payload.uiSessions ?? []).filter(
          (s: UiSession) => {
            return s.state === 'archived' && this.hasSubjectData(s.subjectData);
          },
        );
        const merged = this._mergeSessions(this._sessions(), activeSessions);
        const archivedMerged = this._mergeSessions(
          this._archivedSessions(),
          archivedSessions,
        );
        this._sessions.set(merged);
        this._archivedSessions.set(archivedMerged);
        this._loading.set(false);
        this._initialized.set(true);
        // Auto-select first active session when none is selected (e.g. first visit to myclients)
        if (
          !this._selectedSession() &&
          this.sessions().length > 0 &&
          !this._pendingDeepLinkGuid()
        ) {
          this.createOrSelectClientConversation(this.sessions()[0]);
        }
      },
      error: (err) => {
        this._loading.set(false);
        this._initialized.set(true);
        this._messageService.add({
          severity: 'error',
          summary: 'Failed to get UI Sessions',
          detail: err.message,
        });
      },
    });
  }

  createClientSession(subjectId: string, createRequestId?: string) {
    const clientKey = normalizeClientGuid(subjectId) ?? subjectId.trim();
    const openAttemptKey = `${clientKey}|${createRequestId ?? ''}`;
    const inFlight = this._openAttempts.get(openAttemptKey);
    if (inFlight) return inFlight;

    // BTM keys `ui-session-create-${createRequestId}` for 24h. A fresh UUID
    // per call cannot dedup retries. A first-time open retains its id after a
    // failure, while _openAttempts shares the active request. Re-opening a
    // conversation-less row uses that row's uiSessionId, so it remains a
    // distinct request from a first-time open for the same client.
    const requestId =
      createRequestId ??
      this._openAttemptIds.get(clientKey) ??
      crypto.randomUUID();
    if (!createRequestId) {
      this._openAttemptIds.set(clientKey, requestId);
    }

    const body = {
      subjectType: 'client',
      subjectId,
      createRequestId: requestId,
    };

    const request$: Observable<UiSessionResponse> = this._api
      .put<UiSessionResponse>('api-mobile', '/ui-session', body, {
        telemetryMetadata: { client_guid: clientKey },
      })
      .pipe(
        tap((sessionResponse) => {
          const session = sessionResponse.payload;
          if (
            session.conversationId &&
            !this.hasSubjectData(session.subjectData)
          ) {
            throw new Error(
              'UI session response is missing subject data for the new conversation',
            );
          }
          this._openAttemptIds.delete(clientKey);
          this.addOrUpdateSession(session);
          // CreateUiSession always mints a new UiSessionId. Re-opening a
          // conversation-less row reused that row's id as createRequestId, so
          // the stub stays in Dynamo and in _sessions() unless we retire it.
          if (createRequestId && createRequestId !== session.uiSessionId) {
            this._retireConversationlessRow(createRequestId);
          }
          if (session.conversationId) {
            const guid = session.subjectData.guid;
            this._facade.sendMessage(
              session.subjectId,
              session.conversationId,
              guid,
              undefined,
              this.loggedInUserId(),
            );
            this._amplitude.logEvent('create_conversation', {
              conversationId: session.conversationId,
              uiSessionId: session.uiSessionId,
              subjectGuid: session.subjectData.guid,
              subjectType: 'client',
            });
          }
        }),
        catchError((err) => {
          this._sentry.captureErrorWithExtras(err, {
            client_guid: clientKey,
            source: 'createClientSession',
          });
          this._messageService.add({
            severity: 'error',
            summary: 'Failed to create UI session',
            detail: err.message,
          });
          this._amplitude.logEvent('create_conversation_error', {
            message: err.message,
            subjectGuid: subjectId,
            subjectType: 'client',
            status: err.status,
          });
          return EMPTY;
        }),
        finalize(() => {
          if (this._openAttempts.get(openAttemptKey) === request$) {
            this._openAttempts.delete(openAttemptKey);
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this._openAttempts.set(openAttemptKey, request$);
    return request$;
  }

  addOrUpdateSession(session: UiSession): void {
    const merged = this._mergeSessions(this._sessions(), [session]);
    this._sessions.set(merged);
    this._broadcastSessions();
  }

  private _broadcastSessions() {
    this._channel.postMessage({
      type: 'setSessions',
      payload: this._sessions(),
    });
    this._channel.postMessage({
      type: 'setArchivedSessions',
      payload: this._archivedSessions(),
    });
  }

  private _mergeSessions(
    existing: UiSession[],
    incoming: UiSession[],
  ): UiSession[] {
    const map = new Map(existing.map((s) => [s.uiSessionId, s]));
    for (const session of incoming) {
      map.set(session.uiSessionId, session);
    }
    return Array.from(map.values());
  }
  archiveClientSession(uiSessionId: string) {
    const sessionToDelete = this._sessions().find(
      (s) => s.uiSessionId === uiSessionId,
    );

    this._facade.selectedConversation$
      .pipe(
        take(1),
        tap((conversationId) => {
          this._api
            .del<void>('api-mobile', `/ui-session/${uiSessionId}`)
            .subscribe({
              next: () => {
                const archivedSession = this._sessions().filter(
                  (s) => s.uiSessionId === uiSessionId,
                );
                const updated = this._sessions().filter(
                  (s) => s.uiSessionId !== uiSessionId,
                );

                this._sessions.set(updated);
                this._archivedSessions.set(
                  this._mergeSessions(
                    archivedSession,
                    this._archivedSessions(),
                  ),
                );

                if (sessionToDelete?.subjectId) {
                  this._facade.clearClient(sessionToDelete.subjectId);
                  if (
                    this._selectedSubjectData()?.guid ===
                    sessionToDelete.subjectData.guid
                  ) {
                    this._selectedSubjectData.set(null);
                  }
                }

                if (
                  conversationId &&
                  conversationId === sessionToDelete?.conversationId
                ) {
                  this._facade.clearContext();
                  this._lastActiveSession.set(null);
                }

                this._amplitude.logEvent('archive_conversation', {
                  conversationId: sessionToDelete?.conversationId,
                  uiSessionId: sessionToDelete?.uiSessionId,
                  subjectGuid: sessionToDelete?.subjectData.guid,
                  subjectType: 'client',
                });

                this._broadcastSessions();
              },
              error: (err) => {
                this._messageService.add({
                  severity: 'error',
                  summary: 'Failed to delete UI Session',
                  detail: err.message,
                });
                this._amplitude.logEvent('archive_conversation_error', {
                  message: err.message,
                  conversationId: sessionToDelete?.conversationId,
                  uiSessionId: sessionToDelete?.uiSessionId,
                  subjectGuid: sessionToDelete?.subjectData.guid,
                  subjectType: 'client',
                  status: err.status,
                });
              },
            });
        }),
      )
      .subscribe();
  }

  createOrSelectClientConversation(input: UiSession | string): void {
    if (typeof input === 'string') {
      const normalized = normalizeClientGuid(input);
      if (!normalized) {
        return;
      }
      const existing = this.findActiveSessionByGuid(normalized);
      if (existing) {
        this.createOrSelectClientConversation(existing);
        return;
      }
      this.createClientSession(normalized).subscribe({
        next: (res) => {
          const session = res.payload;
          this._selectedSession.set(session);
          this.setSelectedSubject(session.subjectData);
        },
        error: (err) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Failed to create client session',
            detail: err.message,
          });
        },
      });

      return;
    }

    // If it's a UiSession object
    const session = input;

    if (session.conversationId) {
      this._selectedSession.set(session);
      this.setSelectedSubject(session.subjectData);
      this._facade.clearConversationHighlight(
        session.subjectData.guid,
        session.conversationId,
      );

      this._amplitude.logEvent('select_existing_conversation', {
        conversationId: session.conversationId,
        uiSessionId: session.uiSessionId,
        subjectGuid: session.subjectData.guid,
        subjectType: 'client',
        state: session.state,
      });
      return;
    }

    const live = this.findActiveSessionByGuid(session.subjectData.guid);
    if (live?.conversationId && live.uiSessionId !== session.uiSessionId) {
      this._retireConversationlessRow(session.uiSessionId);
      this.createOrSelectClientConversation(live);
      return;
    }

    this.createClientSession(
      session.subjectData.guid,
      session.uiSessionId,
    ).subscribe({
      next: (res) => {
        const session = res.payload;
        this._selectedSession.set(session);
        this.setSelectedSubject(session.subjectData);
      },
      error: (err) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Failed to create client session',
          detail: err.message,
        });
      },
    });
  }

  hasSubjectData(obj: unknown): obj is ClientSubjectData {
    return !!obj && typeof obj === 'object' && Object.keys(obj).length > 0;
  }

  /**
   * Drop a conversation-less stub after a successful re-open. The create API
   * never updates that row in place, so leaving it would keep it clickable
   * and mint another UiSession against the same BTM conversation.
   * Does not clear copilot context — unlike {@link archiveClientSession}.
   */
  private _retireConversationlessRow(uiSessionId: string): void {
    const inActive = this._sessions().some(
      (s) => s.uiSessionId === uiSessionId,
    );
    const inArchived = this._archivedSessions().some(
      (s) => s.uiSessionId === uiSessionId,
    );
    if (!inActive && !inArchived) {
      return;
    }

    this._sessions.set(
      this._sessions().filter((s) => s.uiSessionId !== uiSessionId),
    );
    this._archivedSessions.set(
      this._archivedSessions().filter((s) => s.uiSessionId !== uiSessionId),
    );
    this._broadcastSessions();

    this._api.del<void>('api-mobile', `/ui-session/${uiSessionId}`).subscribe({
      error: (err) => {
        this._sentry.captureErrorWithExtras(err, {
          source: 'retireConversationlessRow',
          ui_session_id: uiSessionId,
        });
      },
    });
  }

  getUiSession<T = UiSessionsResponse>(
    args?: string,
    body?: unknown,
  ): Observable<T & GenericResponseResult> {
    const path = `/ui-session${args ?? ''}`;
    return this._api.get<T>('api-mobile', path, body).pipe(
      tap((resp: any) => {
        if (resp.result.code === GenericResponseCode.SERVICE_OK) {
          const sessions: UiSession[] = resp.payload.uiSessions || [
            resp.payload.uiSession,
          ];
          this._oktaUserCache.addUsersFromSessions(sessions);
        }
      }),
    );
  }
}
